<template>
  <b-modal
    v-model="isOpen"
    has-modal-card
    trap-focus
    scroll="keep"
    aria-role="dialog"
    aria-label="NotesModale"
    :aria-modal="true"
    class="NotesModale"
    @cancel="emitCloseModale"
  >
    <div class="modal-card">
      <header class="modal-card-head modal-card-head-notes">
        <div class="notes">
          {{ $t("notesModale.title") }} <br />
          <div class="notes-line" />
        </div>
        <div class="notes-grae">
          {{ grae.region }}
        </div>
      </header>
      <section class="modal-card-body">
        <div v-if="grae.dateModification" class="columns">
          <div class="column has-text-right">
            {{ $t("notesModale.modified", [formatDate(grae.dateModification)]).toString() }}
          </div>
        </div>
        <div class="columns">
          <div class="column">
            <InputWithValidation
              type="textarea"
              vid="notes"
              :model-value="localNote"
              maxlength="1400"
              :disabled="!canUserEditNotes"
              @update:model-value="updateLocalNote"
            />
          </div>
        </div>
      </section>

      <footer class="modal-card-foot">
        <div class="buttons">
          <div class="hoverable-discrete-link mr-3" @click="emitCloseModale">
            {{ $t("annuler") }}
          </div>
          <b-button v-if="canUserEditNotes" rounded type="is-primary" @click="saveAndClose">
            {{ $t("notesModale.save") }}
          </b-button>
        </div>
      </footer>
    </div>
  </b-modal>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import { BModal, BButton } from "buefy"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import { GraeDTO } from "@/model/bean/GeneratedDTOs"
import { PROFIL_UTILISATEUR } from "@/model/bean/EnumHelper"
import { LoginService } from "@/services/rest/login/LoginService"
import { GraeService } from "@/services/rest/grae/GraeService"
import { UsersService } from "@/services/rest/users/UsersService"
import DateUtils from "@/services/DateUtils"

export default defineComponent({
  name: "NotesModale",
  components: {
    InputWithValidation,
    BModal,
    BButton,
  },
  props: {
    grae: {
      type: Object as PropType<GraeDTO>,
      required: true,
    },
    open: {
      type: Boolean,
      required: true,
    },
  },

  emits: ["grae-updated", "close-modale"],
  data() {
    return {
      loginService: LoginService.INSTANCE,
      graeService: GraeService.INSTANCE,
      usersService: UsersService.INSTANCE,
      canUserEditNotes: false,
      localNote: "",
    }
  },
  computed: {
    isOpen: {
      get(): boolean {
        return this.open
      },
      set(value: boolean): void {
        if (!value) {
          this.emitCloseModale()
        }
      },
    },
  },
  watch: {
    grae: {
      immediate: true,
      handler(newGrae) {
        if (newGrae) {
          this.localNote = newGrae.note || ""
        }
      },
    },
  },
  created(): void {
    const loggedUser = this.loginService.getLoggedUser()
    this.canUserEditNotes = this.usersService.hasAnyProfile(loggedUser, [
      PROFIL_UTILISATEUR.SUPER_ADMINISTRATEUR,
      PROFIL_UTILISATEUR.ACCOMPAGNATEUR_NATIONAL,
    ])
  },
  methods: {
    formatDate(date: Date): string {
      return DateUtils.formatDate(date)
    },

    updateLocalNote(newValue: string): void {
      this.localNote = newValue
    },

    async saveAndClose(): Promise<void> {
      // Créer une copie mise à jour de l'objet grae avec la nouvelle note
      const updatedGrae = {
        ...this.grae,
        note: this.localNote,
      }

      this.graeService.updateNote(updatedGrae).then(() => {
        // Émettre l'objet mis à jour vers le parent
        const finalUpdatedGrae = {
          ...updatedGrae,
          dateModification: new Date(),
        }
        this.$emit("grae-updated", finalUpdatedGrae)
        this.emitCloseModale()
      })
    },

    emitCloseModale(): void {
      this.$emit("close-modale")
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables";

.title-without-margin {
  .title-view {
    margin-top: 0;
  }
}

.NotesModale {
  &.modal .modal-card {
    height: 75%;
    overflow-y: auto !important;
  }

  .animation-content {
    height: 65%;
  }
}

.modal-card-head-notes {
  display: flex;
  background-color: white !important;

  .notes {
    color: $ofb-bleu-2;
    font-family: $font-family-title;
    font-size: 26px;
    padding-right: 15px;
  }

  .notes-line {
    width: 70px;
    height: 3px;
    background-color: $ofb-bleu-2;
  }

  .notes-grae {
    font-size: 18px;
  }
}
</style>
